// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web, étape 2) — éléments communs aux écrans prêts, aides, trésorerie, comptabilité et
// retenues : bandeaux de démonstration et de simulation, montants, fiches, historique avec auteurs, filtres d'état,
// fenêtre d'acte (erreur du serveur affichée telle quelle), dépôt unique d'une pièce, page de fiche.
// Composants définis hors de tout parent (react-component-rules, règle 1).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import Layout from '../../components/Layout';
import { Badge, Bouton, BandeauInfo, EnTetePage, EtatVide, Squelette } from '../../design/composants';
import { useDs, erreurDe, toArray } from './api';
import { Fenetre, PiedFenetre, Champ, ErreurServeur, AccesRefuseDs, ChoixPiece, pieceComplete, useLecture } from './commun';
import { nouvelleCle, cleARenouveler, routeAbsente, francs, dateHeureLongue, libelleAction } from './apiSocial';

/** Erreur normalisée ; une route encore absente du serveur devient « fonction bientôt disponible ». */
export function erreurSociale(e, defaut) {
  if (routeAbsente(e)) {
    return { statut: 404, code: 'NON_DISPONIBLE', message: 'Fonction bientôt disponible : le serveur ne propose pas encore cette opération.', erreurs: [] };
  }
  return erreurDe(e, defaut);
}

/** Lecture : { donnees, erreur, recharger } (erreur normalisée, route absente signalée). */
export function useLectureSociale(charger, deps) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const recharger = useCallback(async () => {
    try { setErreur(null); setDonnees(await charger()); } catch (e) { setErreur(erreurSociale(e, 'Chargement impossible.')); setDonnees(null); }
  }, deps);
  useEffect(() => { recharger(); }, [recharger]);
  return { donnees, erreur, recharger };
}

// ─── Bandeaux ──────────────────────────────────────────────────────────────────────────────────
export function BandeauComptaDemo() {
  return (
    <BandeauInfo ton="alerte" titre="Comptabilité de démonstration — plan comptable non validé">
      Comptes, journaux et écritures servent à la recette. Aucun état n'a de valeur comptable tant que l'expert-comptable n'a pas validé le plan.
    </BandeauInfo>
  );
}
export function BandeauRetenuesSimulation() {
  return (
    <BandeauInfo ton="alerte" titre="Retenues en simulation — aucune transmission à la Paie">
      Les retenues sont seulement proposées et contrôlées : rien n'est transmis à la Paie ni prélevé sur un salaire.
    </BandeauInfo>
  );
}
/** Marque des données de démonstration. */
export const MarqueDemo = ({ actif }) => (actif ? <Badge ton="alerte" title="Données de démonstration">[DÉMO]</Badge> : null);

// ─── Présentation ──────────────────────────────────────────────────────────────────────────────
export const Montant = ({ n, fort = false }) => <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: fort ? 600 : undefined }}>{francs(n)}</span>;

/** Libellés et valeurs en grille (fiche). `lignes` = [[libellé, valeur], …] ; valeur absente : ligne omise. */
export function Infos({ lignes }) {
  const visibles = lignes.filter((l) => l && l[1] !== null && l[1] !== undefined && l[1] !== '' && l[1] !== false);
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px 24px', margin: 0 }}>
      {visibles.map(([lib, val]) => (
        <div key={lib} style={{ minWidth: 0 }}>
          <dt style={{ fontSize: 12, color: 'var(--sg-texte-3)', marginBottom: 2 }}>{lib}</dt>
          <dd style={{ margin: 0, fontSize: 14, overflowWrap: 'anywhere' }}>{val}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Indicateurs chiffrés (tuiles). `tuiles` = [{ libelle, valeur, aide? }]. */
export function Tuiles({ tuiles }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
      {tuiles.filter(Boolean).map((t) => (
        <div key={t.libelle} className="rh-carte" style={{ margin: 0, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, color: 'var(--sg-texte-3)' }}>{t.libelle}</div>
          <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{t.valeur}</div>
          {t.aide && <div style={{ fontSize: 12, color: 'var(--sg-texte-3)', marginTop: 2 }}>{t.aide}</div>}
        </div>
      ))}
    </div>
  );
}

export const TitreSection = ({ children, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 12px' }}>
    <h2 className="rh-form-section-titre" style={{ margin: 0 }}>{children}</h2>
    {action}
  </div>
);

/** Historique d'un dossier : date, acte, changement d'état, auteur (fonction, intérim), motif. */
export function HistoriqueActes({ lignes, etats = {}, vide = 'Aucun acte enregistré.' }) {
  const l = toArray(lignes);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>{vide}</p>;
  const etat = (c) => (c ? (Array.isArray(etats[c]) ? etats[c][1] : etats[c] || c) : '');
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Date</th><th>Acte</th><th>État</th><th>Par</th><th>Motif</th></tr></thead>
        <tbody>
          {l.map((h, i) => (
            <tr key={`${h.le}-${h.action}-${i}`}>
              <td style={{ whiteSpace: 'nowrap' }}>{dateHeureLongue(h.le)}</td>
              <td>{libelleAction(h.action)}</td>
              <td>{h.de_etat ? `${etat(h.de_etat)} → ${etat(h.vers_etat)}` : etat(h.vers_etat)}</td>
              <td>
                {h.acteur || <span className="rh-cellule-secondaire">Système</span>}
                {h.fonction && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{h.fonction}{h.par_interim ? ', par intérim' : ''}</span>}
              </td>
              <td>{h.motif || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Filtre d'état en pastilles. */
export function FiltreEtats({ table, codes, valeur, onChange, libelleGroupe = 'Filtrer par état' }) {
  return (
    <div className="rh-vues rh-vues-ligne" role="group" aria-label={libelleGroupe} style={{ margin: 0 }}>
      {[['', 'Tous'], ...codes.map((c) => [c, Array.isArray(table[c]) ? table[c][1] : table[c]])].map(([c, l]) => (
        <button key={c || 'tous'} type="button" className="rh-vue" aria-pressed={valeur === c} onClick={() => onChange(c)}>{l}</button>
      ))}
    </div>
  );
}

/** États d'une lecture : refus (403) propre, erreur, chargement, vide expliqué ; sinon les enfants. */
export function EtatsLecture({ lecture, vide, Icone = Inbox, titreVide, estVide, children }) {
  if (lecture.erreur && lecture.erreur.statut === 403) return <AccesRefuseDs erreur={lecture.erreur} />;
  if (lecture.erreur) return <div style={{ padding: 16 }}><ErreurServeur erreur={lecture.erreur} action={<Bouton petit onClick={lecture.recharger}>Réessayer</Bouton>} /></div>;
  if (!lecture.donnees) return <div style={{ padding: 16 }}><Squelette largeur="50%" /><Squelette largeur="70%" style={{ marginTop: 8 }} /></div>;
  if (estVide) return <EtatVide Icone={Icone} titre={titreVide} texte={vide} />;
  return children;
}

// ─── Actes ─────────────────────────────────────────────────────────────────────────────────────
/**
 * Fenêtre d'un acte : champs fournis par l'appelant (composant défini hors du parent), bouton actif quand `pret`,
 * clé d'idempotence stable pour la fenêtre (renvoyée à `onConfirmer(cle)`), refus du serveur affiché tel quel.
 * La clé est GARDÉE après une coupure réseau, un délai dépassé, une erreur 5xx ou 409 IDEMPOTENCE_EN_COURS (le renvoi
 * reçoit alors la réponse d'origine) ; elle est RENOUVELÉE après toute autre réponse 4xx (le serveur peut avoir mémorisé
 * ce refus : la personne corrige et renvoie une nouvelle demande). docs/rh/securite/idempotence/MECANISME.md.
 */
export function FenetreActe({ titre, Icone, aide, libelleBouton, pret = true, danger = false, large = false, onFermer, onConfirmer, children }) {
  const [cle, setCle] = useState(nouvelleCle);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const valider = async () => {
    setEnCours(true); setErreur(null);
    try { await onConfirmer(cle); } catch (e) {
      if (cleARenouveler(e)) setCle(nouvelleCle());
      setErreur(e.normalisee || erreurSociale(e)); setEnCours(false);
    }
  };
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer} large={large}>
      <ErreurServeur erreur={erreur} />
      {aide && <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>{aide}</p>}
      {children}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante={danger ? 'danger' : 'primaire'} Icone={Icone} disabled={enCours || !pret} onClick={valider}>{libelleBouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export const motifValide = (m, min = 5) => String(m || '').trim().length >= min;

/** Pièce justificative déposée UNE fois (une pièce ne se supprime jamais), même si l'acte qui suit échoue. */
export function usePieceDepot(organisationId, sujetType, sujetId, typeDefaut = 'JUSTIFICATIF') {
  const ds = useDs();
  const [saisie, setSaisie] = useState({ fichier: null, typePiece: typeDefaut, libelle: '' });
  const [deposee, setDeposee] = useState(null);
  const deposer = async () => {
    if (deposee) return deposee;
    try {
      const p = await ds.deposerPiece({ fichier: saisie.fichier, organisationId, sujetType, sujetId, typePiece: saisie.typePiece, libelle: saisie.libelle.trim() });
      setDeposee(p);
      return p;
    } catch (e) { throw Object.assign(new Error('piece'), { normalisee: erreurSociale(e, 'Dépôt de la pièce impossible.') }); }
  };
  return { saisie, setSaisie, deposee, deposer, prete: !!deposee || pieceComplete(saisie) };
}

/** Champ de dépôt d'une pièce (réutilise ChoixPiece du socle). */
export const ChampPiece = ({ piece, types, libelleChamp }) => (
  <ChoixPiece valeur={piece.saisie} onChange={piece.setSaisie} types={types} deposee={piece.deposee} libelleChamp={libelleChamp} />
);

export function ChampMotif({ valeur, onChange, requis = true, libelleChamp = 'Motif', aide = '5 caractères au moins. Le motif est conservé dans l\'historique.' }) {
  return (
    <Champ libelle={libelleChamp} requis={requis} aide={aide}>
      <textarea className="rh-champ-saisie" rows={3} value={valeur} onChange={(e) => onChange(e.target.value)} />
    </Champ>
  );
}

export function ChoixRadio({ nom, options, valeur, onChange }) {
  return (
    <div role="radiogroup" style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
      {Object.entries(options).map(([c, l]) => (
        <label key={c} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input type="radio" name={nom} value={c} checked={valeur === c} onChange={() => onChange(c)} />{l}
        </label>
      ))}
    </div>
  );
}

// ─── Page de fiche ─────────────────────────────────────────────────────────────────────────────
/** Page d'une fiche (prêt, aide, créance, paiement) : fil d'Ariane jusqu'à l'onglet de l'organisation. */
export function PageFiche({ section, libelleSection, titre, actions, children }) {
  const { org } = useParams();
  const ds = useDs();
  const o = useLecture(() => ds.organisation(org), [ds, org]);
  const fil = [
    { libelle: 'Dispositifs sociaux', vers: '/dispositifs' },
    { libelle: o.donnees?.libelle || 'Organisation', vers: `/dispositifs/${org}/${section}` },
    { libelle: libelleSection, vers: `/dispositifs/${org}/${section}` },
    { libelle: titre || '…' },
  ];
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={fil} titre={titre || libelleSection} actions={actions} />
        {children}
      </div>
    </Layout>
  );
}
